import { Injectable, signal, computed } from '@angular/core';
import { jwtDecode } from 'jwt-decode';
import { EOvrheJwt } from '../../interfaces/eOvrheJwt';
import { Certificate } from '@/app/interfaces/digitalSigning';

@Injectable({
  providedIn: 'root',
})
export class SettingsService {
  private readonly eOvrheStorageKey = 'eOvrheApiKey';
  private readonly eKomunikacijeStorageKey = 'eKomunikacijeApiKey';
  private readonly saveDirectoryKey = 'eOvrheSaveDirectory';

  private readonly selectedCertificateKey = 'selectedCertificate';

  private readonly troskovnikVrstaPodneskaIdKey = 'troskovnikVrstaPodneskaId';
  private readonly troskovnikUlogaPodnositeljaIdKey =
    'troskovnikUlogaPodnositeljaId';
  private readonly troskovnikUlogaStrankeIdKey = 'troskovnikUlogaStrankeId';
  private readonly troskovnikUlogaProtustrankeIdKey =
    'troskovnikUlogaProtustrankeId';

  private readonly finaBatchConcurrencyKey = 'finaBatchConcurrency';
  private readonly finaBatchRetryCountKey = 'finaBatchRetryCount';
  private readonly finaBatchConcurrencyDefault = 5;
  private readonly finaBatchConcurrencyMin = 1;
  private readonly finaBatchConcurrencyMax = 10;
  private readonly finaBatchRetryCountDefault = 2;
  private readonly finaBatchRetryCountMin = 0;
  private readonly finaBatchRetryCountMax = 5;

  eOvrheApiKey = signal<string | null>(this.getEovrheApiKey());
  eKomunikacijeApiKey = signal<string | null>(this.getEkomunikacijeApiKey());

  troskovnikVrstaPodneskaId = signal<number | null>(
    this.readNumber(this.troskovnikVrstaPodneskaIdKey),
  );
  troskovnikUlogaPodnositeljaId = signal<number | null>(
    this.readNumber(this.troskovnikUlogaPodnositeljaIdKey),
  );
  troskovnikUlogaStrankeId = signal<number | null>(
    this.readNumber(this.troskovnikUlogaStrankeIdKey),
  );
  troskovnikUlogaProtustrankeId = signal<number | null>(
    this.readNumber(this.troskovnikUlogaProtustrankeIdKey),
  );

  finaBatchConcurrency = signal<number>(
    this.readClampedNumber(
      this.finaBatchConcurrencyKey,
      this.finaBatchConcurrencyDefault,
      this.finaBatchConcurrencyMin,
      this.finaBatchConcurrencyMax,
    ),
  );
  finaBatchRetryCount = signal<number>(
    this.readClampedNumber(
      this.finaBatchRetryCountKey,
      this.finaBatchRetryCountDefault,
      this.finaBatchRetryCountMin,
      this.finaBatchRetryCountMax,
    ),
  );

  saveDirectory = signal<string | null>(
    localStorage.getItem(this.saveDirectoryKey),
  );

  selectedCertificate = signal<Certificate | null>(
    this.getStoredSelectedCertificate(),
  );

  isEovrheJwtValid = computed<boolean>(() => {
    const token = this.eOvrheApiKey();
    if (!token) return false;

    const decoded = this.decodeJwt(token);
    if (!decoded || !decoded.exp) return false;

    const currentTime = Math.floor(Date.now() / 1000);
    return decoded.exp > currentTime;
  });

  isEKomunikacijaValid = computed<boolean>(() => {
    const token = this.eKomunikacijeApiKey();
    if (!token) return false;

    const decoded = this.decodeJwt(token);
    if (!decoded || !decoded.exp) return false;

    const currentTime = Math.floor(Date.now() / 1000);
    return decoded.exp > currentTime;
  });

  eOvrheJwtStatus = computed<string>(() => {
    const token = this.eOvrheApiKey();
    if (!token) return 'API ključ nije unesen.';

    return this.isEovrheJwtValid()
      ? 'E-Ovrhe API ključ je valjan i nije istekao.'
      : 'E-Ovrhe API ključ je nevažeći ili je istekao.';
  });

  eKomunikacijaJwtStatus = computed<string>(() => {
    const token = this.eKomunikacijeApiKey();
    if (!token) return 'API ključ nije unesen.';

    return this.isEKomunikacijaValid()
      ? 'E-Komunikacija API ključ je valjan i nije istekao.'
      : 'E-Komunikacija API ključ je nevažeći ili je istekao.';
  });

  jwtDecoded = computed<EOvrheJwt | null>(() => {
    const token = this.eOvrheApiKey();
    return token ? this.decodeJwt(token) : null;
  });

  eKomunikacijaJwtDecoded = computed<EOvrheJwt | null>(() => {
    const token = this.eKomunikacijeApiKey();
    return token ? this.decodeJwt(token) : null;
  });

  setEovrheApiKey(apiKey: string): void {
    localStorage.setItem(this.eOvrheStorageKey, apiKey);
    this.eOvrheApiKey.set(apiKey);
  }

  setEkomunikacijeApiKey(apiKey: string): void {
    localStorage.setItem(this.eKomunikacijeStorageKey, apiKey);
    this.eKomunikacijeApiKey.set(apiKey);
  }

  setSaveDirectory(path: string): void {
    localStorage.setItem(this.saveDirectoryKey, path);
    this.saveDirectory.set(path);
  }

  setTroskovnikVrstaPodneskaId(id: number): void {
    localStorage.setItem(this.troskovnikVrstaPodneskaIdKey, String(id));
    this.troskovnikVrstaPodneskaId.set(id);
  }

  setTroskovnikUlogaPodnositeljaId(id: number): void {
    localStorage.setItem(this.troskovnikUlogaPodnositeljaIdKey, String(id));
    this.troskovnikUlogaPodnositeljaId.set(id);
  }

  setTroskovnikUlogaStrankeId(id: number): void {
    localStorage.setItem(this.troskovnikUlogaStrankeIdKey, String(id));
    this.troskovnikUlogaStrankeId.set(id);
  }

  setTroskovnikUlogaProtustrankeId(id: number): void {
    localStorage.setItem(this.troskovnikUlogaProtustrankeIdKey, String(id));
    this.troskovnikUlogaProtustrankeId.set(id);
  }

  setFinaBatchConcurrency(value: number): void {
    const clamped = this.clamp(
      value,
      this.finaBatchConcurrencyMin,
      this.finaBatchConcurrencyMax,
    );
    localStorage.setItem(this.finaBatchConcurrencyKey, String(clamped));
    this.finaBatchConcurrency.set(clamped);
  }

  setFinaBatchRetryCount(value: number): void {
    const clamped = this.clamp(
      value,
      this.finaBatchRetryCountMin,
      this.finaBatchRetryCountMax,
    );
    localStorage.setItem(this.finaBatchRetryCountKey, String(clamped));
    this.finaBatchRetryCount.set(clamped);
  }

  setSelectedCertificate(certificate: Certificate): void {
    localStorage.setItem(
      this.selectedCertificateKey,
      JSON.stringify(certificate),
    );
    this.selectedCertificate.set(certificate);
  }

  clearSelectedCertificate(): void {
    localStorage.removeItem(this.selectedCertificateKey);
    this.selectedCertificate.set(null);
  }

  private getStoredSelectedCertificate(): Certificate | null {
    const stored = localStorage.getItem(this.selectedCertificateKey);
    if (!stored) return null;

    try {
      return JSON.parse(stored) as Certificate;
    } catch (e) {
      console.error('Error parsing stored certificate', e);
      return null;
    }
  }

  hasRequiredSettings = computed(() => {
    return this.isEovrheJwtValid() && !!this.saveDirectory();
  });

  hasEkomunikacijaSettings = computed(() => {
    return this.isEKomunikacijaValid() && !!this.saveDirectory();
  });

  private getEovrheApiKey(): string | null {
    return localStorage.getItem(this.eOvrheStorageKey);
  }

  private getEkomunikacijeApiKey(): string | null {
    return localStorage.getItem(this.eKomunikacijeStorageKey);
  }

  private readNumber(key: string): number | null {
    const raw = localStorage.getItem(key);
    if (raw === null) return null;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
  }

  private readClampedNumber(
    key: string,
    fallback: number,
    min: number,
    max: number,
  ): number {
    const value = this.readNumber(key);
    return value === null ? fallback : this.clamp(value, min, max);
  }

  private clamp(value: number, min: number, max: number): number {
    const rounded = Math.round(value);
    if (!Number.isFinite(rounded)) return min;
    return Math.min(max, Math.max(min, rounded));
  }

  private decodeJwt(token: string): EOvrheJwt | null {
    try {
      return jwtDecode<EOvrheJwt>(token);
    } catch (e) {
      console.error('Invalid JWT', e);
      return null;
    }
  }
}
